import { useState, useEffect, useRef } from "react";
import {
  IcCart, IcClipboard, IcClock, IcMemo, IcBox, IcCash, IcCard, IcEdit, IcWarning, IcMoney,
} from "./Icons";

// "" → relative URL: dev'de CRA proxy (package.json "proxy"), prod'da Nginx same-origin
const API = process.env.REACT_APP_API_URL ?? "";

// Para formatı: 180 → "180", 129.5 → "129,5", 1500 → "1.500"
const fmtTL = v => Number(v || 0).toLocaleString("tr-TR", { minimumFractionDigits: 0, maximumFractionDigits: 2 });

// ── Gramaj Giriş Modalı (EditOrderModal için) ─────────────────────────────────
function EomWeightModal({ product, onConfirm, onCancel }) {
  const [grams, setGrams] = useState(product.initial_grams ? String(product.initial_grams) : "");
  const gramsNum    = parseInt(grams, 10);
  const previewPrice = (!isNaN(gramsNum) && gramsNum > 0)
    ? (product.price * gramsNum / 100).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : null;

  return (
    <div className="mm-weight-overlay" onClick={onCancel}>
      <div className="mm-weight-dialog" onClick={e => e.stopPropagation()}>
        <div className="mm-weight-title">{product.image_emoji} {product.name}</div>
        <div className="mm-weight-hint">Birim fiyat: ₺{product.price} / 100g</div>
        <div className="mm-weight-row">
          <input
            type="number"
            className="mm-weight-input"
            placeholder="Gramaj"
            value={grams}
            onChange={e => setGrams(e.target.value)}
            onKeyDown={e => {
              if (e.key === "Enter" && gramsNum > 0) onConfirm(gramsNum);
              if (e.key === "Escape") onCancel();
            }}
            autoFocus
            min="1"
          />
          <span className="mm-weight-unit">g</span>
        </div>
        {previewPrice && <div className="mm-weight-preview">= ₺{previewPrice}</div>}
        <div className="mm-weight-actions">
          <button className="mm-confirm-cancel" onClick={onCancel}>İptal</button>
          <button className="mm-confirm-ok" disabled={!gramsNum || gramsNum < 1} onClick={() => onConfirm(gramsNum)}>
            Sepete Ekle
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Order Edit Modal ──────────────────────────────────────────────────────────
function EditOrderModal({ order, onClose, onSaved }) {
  const [menu,           setMenu]           = useState([]);
  const [cart,           setCart]           = useState({});
  const [note,           setNote]           = useState(order.note || "");
  const [openCats,       setOpenCats]       = useState({});
  const [saving,         setSaving]         = useState(false);

  // Kategori accordion scroll — açılan kategoriye smooth scroll
  const catRefs         = useRef({});
  const scrollPendingCat = useRef(null);
  useEffect(() => {
    if (scrollPendingCat.current != null) {
      const el = catRefs.current[scrollPendingCat.current];
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      scrollPendingCat.current = null;
    }
  }, [openCats]);
  const [error,          setError]          = useState("");
  const [loadingMenu,    setLoadingMenu]    = useState(true);
  const [confirmSave,    setConfirmSave]    = useState(false);
  const [weightInput,    setWeightInput]    = useState(null); // product | null
  const [weightPids,     setWeightPids]     = useState(new Set()); // Set<number> — menüden gelen is_weight_based=1 ürün id'leri

  // Menüyü çek, ardından cart'ı tek seferde doğru kur
  useEffect(() => {
    setLoadingMenu(true);
    fetch(`${API}/api/menu`)
      .then(r => r.json())
      .then(d => {
        const cats = d.categories || [];
        setMenu(cats);

        // Menüden weight-based ürün id'lerini al
        const weightIds = new Set(
          cats.flatMap(c => c.products || [])
              .filter(p => p.is_weight_based)
              .map(p => p.id)
        );
        setWeightPids(weightIds);

        // Cart'ı menü verisiyle birlikte tek seferde oluştur.
        // is_weight_based: DB'de weight_grams varsa VEYA menü is_weight_based=1 diyorsa true.
        const initial = {};
        (order.items || []).forEach(item => {
          initial[item.product_id] = {
            product_name:    item.product_name,
            product_price:   item.product_price,
            qty:             item.quantity,
            item_note:       item.item_note  || null,
            weight_grams:    item.weight_grams || null,
            is_weight_based: !!(item.weight_grams) || weightIds.has(item.product_id),
          };
        });
        setCart(initial);
        setLoadingMenu(false);
      })
      .catch((err) => { console.error('[EOM] menu fetch failed:', err); setLoadingMenu(false); });
  }, [order.id]); // eslint-disable-line

  // ESC tuşu ile kapat (onay açıksa onayı kapat, değilse modalı kapat)
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") {
        if (confirmSave) setConfirmSave(false);
        else onClose();
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [confirmSave]); // eslint-disable-line

  const changeQty = (productId, productName, productPrice, delta) => {
    setCart(prev => {
      const cur  = prev[productId]?.qty || 0;
      const next = cur + delta;
      if (next <= 0) {
        const { [productId]: _, ...rest } = prev;
        return rest;
      }
      return {
        ...prev,
        [productId]: {
          ...prev[productId],
          product_name:  productName,
          product_price: productPrice,
          qty:           next,
        },
      };
    });
  };

  const addWeightItem = (product, grams) => {
    setCart(prev => ({
      ...prev,
      [product.id]: {
        product_name:    product.name,
        product_price:   product.price,
        qty:             1,
        weight_grams:    grams,
        is_weight_based: true,
      },
    }));
    setWeightInput(null);
  };

  const removeCartItem = (productId) => {
    setCart(prev => { const { [productId]: _, ...rest } = prev; return rest; });
  };

  const total     = Object.entries(cart).reduce((s, [pid, i]) => s + ((i.is_weight_based || weightPids.has(Number(pid))) ? (i.weight_grams || 0) * i.product_price / 100 : i.qty * i.product_price), 0);
  const itemCount = Object.entries(cart).reduce((s, [pid, i]) => s + ((i.is_weight_based || weightPids.has(Number(pid))) ? 1 : i.qty), 0);

  const handleSave = async () => {
    if (itemCount === 0) { setError("En az 1 ürün olmalı"); return; }
    if (!confirmSave) { setConfirmSave(true); return; } // Onay adımı
    setSaving(true);
    setError("");
    const items = Object.entries(cart).map(([id, v]) => {
      const item = { product_id: Number(id), quantity: v.qty, item_note: v.item_note || null };
      if (v.is_weight_based || weightPids.has(Number(id))) item.weight_grams = v.weight_grams;
      return item;
    });
    try {
      const res = await fetch(`${API}/api/orders/${order.id}`, {
        method:  "PUT",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ items, note: note || null }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.detail || "Düzenleme başarısız");
        setConfirmSave(false);
      } else {
        onSaved();
        onClose();
      }
    } catch {
      setError("Bağlantı hatası");
      setConfirmSave(false);
    }
    setSaving(false);
  };

  return (
    <div className="modal-overlay">
      <div className="edit-order-modal">
        <div className="eom-header">
          <div className="eom-title" style={{display:"inline-flex",alignItems:"center",gap:8}}><IcEdit size={16} /> Sipariş Düzenle #{order.id}</div>
          <button className="eom-close" onClick={onClose}>✕</button>
        </div>

        {loadingMenu ? (
          <div className="eom-loading">Menü yükleniyor…</div>
        ) : (
          <div className="eom-body">
            {/* Mevcut sepet */}
            {itemCount > 0 && (
              <div className="eom-section">
                <div className="eom-section-title"><IcCart size={15} /> Sepet · {itemCount} ürün</div>
                <div className="eom-cart-section">
                  {Object.entries(cart).map(([pid, v]) => (
                    <div key={pid} className="eom-cart-row">
                      <span className="eom-cart-name">{v.product_name}</span>
                      {(v.is_weight_based || weightPids.has(Number(pid))) ? (
                        <>
                          {v.weight_grams
                            ? <span className="eom-cart-price">₺{(v.weight_grams * v.product_price / 100).toLocaleString("tr-TR", { maximumFractionDigits: 2 })}</span>
                            : <span className="eom-cart-price eom-cart-price--warn">Gramaj girilmedi</span>
                          }
                          <div className="eom-cart-qty">
                            <button className="eom-qty-btn" title="Gramajı düzenle"
                              onClick={() => setWeightInput({ id: Number(pid), name: v.product_name, price: v.product_price, image_emoji: "", initial_grams: v.weight_grams })}>✎</button>
                            <span className="eom-qty-val">{v.weight_grams ? `${v.weight_grams}g` : "—"}</span>
                            <button className="eom-qty-btn" onClick={() => removeCartItem(Number(pid))}>✕</button>
                          </div>
                        </>
                      ) : (
                        <>
                          <span className="eom-cart-price">₺{(v.product_price * v.qty).toFixed(0)}</span>
                          <div className="eom-cart-qty">
                            <button className="eom-qty-btn" onClick={() => changeQty(Number(pid), v.product_name, v.product_price, -1)}>−</button>
                            <span className="eom-qty-val">{v.qty}</span>
                            <button className="eom-qty-btn" onClick={() => changeQty(Number(pid), v.product_name, v.product_price, +1)}>+</button>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Ürün ekle (accordion) */}
            <div className="eom-section">
              <div className="eom-section-title">Ürün Ekle / Değiştir</div>
              <div className="eom-accordion">
                {menu.map(cat => {
                  const isOpen = openCats[cat.id] || false;
                  return (
                    <div key={cat.id} ref={el => { catRefs.current[cat.id] = el; }} className="eom-acc-item">
                      <button
                        className={`eom-acc-header${isOpen ? " open" : ""}`}
                        onClick={() => {
                          if (!isOpen) scrollPendingCat.current = cat.id;
                          setOpenCats(prev => ({ ...prev, [cat.id]: !isOpen }));
                        }}
                      >
                        <span>{cat.emoji} {cat.name}</span>
                        <span className="eom-acc-arrow">{isOpen ? "▼" : "▶"}</span>
                      </button>
                      {isOpen && (
                        <div className="eom-acc-body">
                          {cat.products.map(p => {
                            const inCart = !!cart[p.id];
                            const qty    = cart[p.id]?.qty || 0;
                            return (
                              <div key={p.id} className={`eom-product${inCart ? " in-cart" : ""}`}>
                                <span className="eom-p-name">{p.name}</span>
                                {p.is_weight_based ? (
                                  <>
                                    <span className="eom-p-price">₺{p.price}/100g</span>
                                    <div className="eom-p-qty">
                                      {inCart && <span className="mm-ci-weight-badge">{cart[p.id].weight_grams}g</span>}
                                      <button className="eom-qty-btn plus"
                                        onClick={() => setWeightInput({ ...p, initial_grams: cart[p.id]?.weight_grams })}>
                                        {inCart ? "✎" : "+"}
                                      </button>
                                      {inCart && <button className="eom-qty-btn" onClick={() => removeCartItem(p.id)}>✕</button>}
                                    </div>
                                  </>
                                ) : (
                                  <>
                                    <span className="eom-p-price">₺{p.price}</span>
                                    <div className="eom-p-qty">
                                      {qty > 0 && <button className="eom-qty-btn" onClick={() => changeQty(p.id, p.name, p.price, -1)}>−</button>}
                                      {qty > 0 && <span className="eom-qty-val">{qty}</span>}
                                      <button className="eom-qty-btn plus" onClick={() => changeQty(p.id, p.name, p.price, +1)}>+</button>
                                    </div>
                                  </>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Not */}
            <div className="eom-section">
              <div className="eom-section-title">Sipariş Notu</div>
              <textarea
                className="eom-note"
                value={note}
                onChange={e => setNote(e.target.value)}
                placeholder="Sipariş notu…"
                rows={2}
              />
            </div>
          </div>
        )}

        {error && <div className="eom-error">{error}</div>}

        {/* Gramaj giriş modalı */}
        {weightInput && (
          <EomWeightModal
            product={weightInput}
            onConfirm={(grams) => addWeightItem(weightInput, grams)}
            onCancel={() => setWeightInput(null)}
          />
        )}

        {/* Onay alt-modal */}
        {confirmSave && !saving && (
          <div className="eom-confirm-overlay">
            <div className="eom-confirm-card">
              <div className="eom-confirm-icon-big"><IcEdit size={32} /></div>
              <div className="eom-confirm-title-big">Sipariş güncellenecek</div>
              <div className="eom-confirm-sub-text">Bu değişiklik kaydedilecek.<br />Onaylıyor musunuz?</div>
              <div className="eom-confirm-btns-row">
                <button className="eom-cancel-btn eom-confirm-no" onClick={() => setConfirmSave(false)}>Hayır</button>
                <button className="eom-save-btn" style={{flex:1}} onClick={handleSave}>✓ Güncelle</button>
              </div>
            </div>
          </div>
        )}

        <div className="eom-footer">
          <div className="eom-total">
            <span>{itemCount} ürün</span>
            <strong>₺{fmtTL(total)}</strong>
          </div>
          <div className="eom-footer-btns">
            <button className="eom-cancel-btn" onClick={onClose} disabled={saving}>İptal</button>
            {!confirmSave && (
              <button
                className="eom-save-btn"
                onClick={handleSave}
                disabled={saving || itemCount === 0}
              >
                {saving ? "Kaydediliyor…" : "✓ Kaydet"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Move Table Modal ──────────────────────────────────────────────────────────
function MoveTableModal({ sessionId, currentTableId, currentTableLabel, onMoved, onClose }) {
  const [tables,  setTables]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [moving,  setMoving]  = useState(false);
  const [error,   setError]   = useState(null);
  const [confirm, setConfirm] = useState(null); // { id, label }

  useEffect(() => {
    const load = async () => {
      try {
        const [tablesRes, openRes, partialRes] = await Promise.all([
          fetch(`${API}/api/admin/tables`,                  { credentials: "include" }),
          fetch(`${API}/api/sessions?status=OPEN`,          { credentials: "include" }),
          fetch(`${API}/api/sessions?status=PARTIALLY_PAID`,{ credentials: "include" }),
        ]);
        const tablesData  = await tablesRes.json();
        const openData    = await openRes.json();
        const partialData = await partialRes.json();

        const occupiedIds = new Set([
          ...(openData.sessions    || []).map(s => s.table_id),
          ...(partialData.sessions || []).map(s => s.table_id),
        ]);

        setTables(
          (tablesData.tables || []).filter(t =>
            t.is_active === 1 &&
            t.id !== currentTableId &&
            !occupiedIds.has(t.id)
          )
        );
      } catch { setError("Masalar yüklenemedi"); }
      setLoading(false);
    };
    load();
  }, [currentTableId]);

  const handleMove = async (targetTableId) => {
    setMoving(true);
    setError(null);
    try {
      const res = await fetch(`${API}/api/sessions/${sessionId}/move-table`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ target_table_id: targetTableId }),
      });
      const data = await res.json();
      if (res.ok) { onMoved(data); }
      else         { setError(data.detail || "Taşıma başarısız"); setMoving(false); }
    } catch { setError("Bağlantı hatası"); setMoving(false); }
  };

  const ic  = tables.filter(t => t.location === "ic");
  const dis = tables.filter(t => t.location === "dis");

  if (confirm) {
    return (
      <div className="confirm-overlay" onClick={() => setConfirm(null)}>
        <div className="move-table-modal" onClick={e => e.stopPropagation()}>
          <div className="mtm-title">↔ Masayı Taşı</div>
          <div className="mtm-confirm-text">
            <span className="mtm-confirm-from">{currentTableLabel}</span>
            <span className="mtm-confirm-arrow"> → </span>
            <span className="mtm-confirm-to">{confirm.label}</span>
          </div>
          <div className="mtm-confirm-question">Emin misiniz?</div>
          {error && <div className="mtm-error">{error}</div>}
          <div className="mtm-confirm-btns">
            <button className="mtm-cancel" onClick={() => setConfirm(null)} disabled={moving}>İptal</button>
            <button className="mtm-confirm-ok" onClick={() => handleMove(confirm.id)} disabled={moving}>
              {moving ? "Taşınıyor…" : "Evet, Taşı"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="confirm-overlay" onClick={onClose}>
      <div className="move-table-modal" onClick={e => e.stopPropagation()}>
        <div className="mtm-title">↔ Masayı Taşı</div>
        {currentTableLabel && (
          <div className="mtm-current-label">{currentTableLabel}</div>
        )}
        <div className="mtm-sub">Boş masayı seç</div>
        {loading ? (
          <div className="mtm-loading">Yükleniyor…</div>
        ) : tables.length === 0 ? (
          <div className="mtm-empty">Uygun boş masa yok</div>
        ) : (
          <div className="mtm-groups">
            {ic.length > 0 && (
              <div className="mtm-group">
                <div className="mtm-group-label">İç</div>
                <div className="mtm-list">
                  {ic.map(t => (
                    <button key={t.id} className="mtm-table-btn" onClick={() => setConfirm({ id: t.id, label: t.label })} disabled={moving}>
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {dis.length > 0 && (
              <div className="mtm-group">
                <div className="mtm-group-label">Dış</div>
                <div className="mtm-list">
                  {dis.map(t => (
                    <button key={t.id} className="mtm-table-btn" onClick={() => setConfirm({ id: t.id, label: t.label })} disabled={moving}>
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        {error && <div className="mtm-error">{error}</div>}
        <button className="mtm-cancel" onClick={onClose} disabled={moving}>İptal</button>
      </div>
    </div>
  );
}


function ConfirmPay({ amount, method, label, onConfirm, onCancel, paying }) {
  const [copied, setCopied] = useState(false);
  const isCard = method === "CARD";

  const copyAmount = () => {
    navigator.clipboard?.writeText(fmtTL(amount)).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {});
  };

  // Kart: POS cihazına tutar girilirken okunacak büyük ekran
  if (isCard) {
    return (
      <div className="confirm-overlay" onClick={onCancel}>
        <div className="confirm-box pos-box" onClick={e => e.stopPropagation()}>
          {label && <div className="confirm-title">{label}</div>}
          <div className="pos-label"><IcCard size={15} /> POS Cihazına Girin</div>
          <div className="pos-amount" onClick={copyAmount} title="Kopyalamak için dokun">
            ₺{fmtTL(amount)}
          </div>
          <button className="pos-copy" onClick={copyAmount}>
            {copied ? "✓ Kopyalandı" : "Tutarı kopyala"}
          </button>
          <div className="pos-hint">
            POS'tan <b>onay çıktıktan sonra</b> Onayla'ya basın
          </div>
          <div className="confirm-btns">
            <button className="confirm-btn cancel" onClick={onCancel} disabled={paying}>İptal</button>
            <button className="confirm-btn ok" onClick={onConfirm} disabled={paying}>
              {paying ? "İşleniyor…" : "✓ Onayla"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Nakit: mevcut kompakt onay
  return (
    <div className="confirm-overlay" onClick={onCancel}>
      <div className="confirm-box" onClick={e => e.stopPropagation()}>
        <div className="confirm-icon"><IcCash size={30} /></div>
        <div className="confirm-title">{label || "Ödeme Alınıyor"}</div>
        <div className="confirm-amount">₺{fmtTL(amount)}</div>
        <div className="confirm-method">Nakit</div>
        <div className="confirm-btns">
          <button className="confirm-btn cancel" onClick={onCancel} disabled={paying}>İptal</button>
          <button className="confirm-btn ok" onClick={onConfirm} disabled={paying}>
            {paying ? "İşleniyor…" : "✓ Onayla"}
          </button>
        </div>
      </div>
    </div>
  );
}

function SplitFlow({ splitPlan, setSplitPlan, onAllPaid, sessionId, onCancel }) {
  const [paying, setPaying]   = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [payMsg, setPayMsg]   = useState(null);
  const currentIdx = splitPlan.findIndex(p => !p.paid);

  const executePay = async () => {
    const idx  = confirm;
    const item = splitPlan[idx];
    setPaying(true);
    setPayMsg(null);
    // Popup blocker'ı aşmak için pencereyi fetch'ten ÖNCE aç — SADECE son ödeme ise.
    // Ara ödemelerde pencere açılıp kapanmasın (kullanıcı "adisyon çıktı" zannetmesin).
    const isLastSplit = splitPlan.filter(p => !p.paid).length === 1;
    const receiptWin = isLastSplit ? window.open("about:blank", "_blank") : null;
    try {
      const res = await fetch(`${API}/api/sessions/${sessionId}/pay`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          amount: item.amount,
          payment_method: item.method,
          idempotency_key: item.idempotencyKey,
          split_index: idx + 1,
          split_total: splitPlan.length,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        if (receiptWin) receiptWin.close();
        setPayMsg({ err: true, text: data.detail || "Ödeme başarısız" });
      } else {
        const updated = splitPlan.map((p, i) => i === idx ? { ...p, paid: true } : p);
        setSplitPlan(updated);
        if (data.is_fully_paid) {
          if (data.auto_print && receiptWin) {
            receiptWin.location.href = `${API}/api/sessions/${sessionId}/receipt?autoprint=true`;
          } else if (receiptWin) {
            receiptWin.close();
          }
          setTimeout(() => onAllPaid(), 600);
        } else {
          if (receiptWin) receiptWin.close();
        }
      }
    } catch {
      if (receiptWin) receiptWin.close();
      setPayMsg({ err: true, text: "Bağlantı hatası" });
    }
    setConfirm(null);
    setPaying(false);
  };

  return (
    <div className="split-flow">
      {confirm !== null && (
        <ConfirmPay
          amount={splitPlan[confirm].amount}
          method={splitPlan[confirm].method}
          label={`${confirm + 1}. Kişi Ödemesi`}
          onConfirm={executePay}
          onCancel={() => setConfirm(null)}
          paying={paying}
        />
      )}
      <div className="split-flow-header">
        <span>{splitPlan.filter(p => p.paid).length} / {splitPlan.length} ödeme alındı</span>
        <button className="split-cancel-btn" onClick={onCancel}>← Geri</button>
      </div>
      <div className="split-progress">
        {splitPlan.map((p, i) => (
          <div key={i} className={`split-progress-dot${p.paid ? " paid" : i === currentIdx ? " current" : ""}`} />
        ))}
      </div>
      <div className="split-items">
        {splitPlan.map((p, i) => (
          <div key={i} className={`split-item${p.paid ? " paid" : i === currentIdx ? " current" : " waiting"}`}>
            <div className="split-item-left">
              <span className="split-item-num">{i + 1}. Kişi</span>
              <span className="split-item-amount">₺{fmtTL(p.amount)}</span>
              {!p.paid && (
                <div className="split-item-method-row">
                  <button
                    className={`split-method-mini${p.method === "CASH" ? " active" : ""}`}
                    onClick={() => setSplitPlan(prev => prev.map((x, j) => j === i ? { ...x, method: "CASH" } : x))}
                    disabled={paying}
                  ><span style={{display:"inline-flex",alignItems:"center",gap:5}}><IcCash size={13} /> Nakit</span></button>
                  <button
                    className={`split-method-mini${p.method === "CARD" ? " active" : ""}`}
                    onClick={() => setSplitPlan(prev => prev.map((x, j) => j === i ? { ...x, method: "CARD" } : x))}
                    disabled={paying}
                  ><span style={{display:"inline-flex",alignItems:"center",gap:5}}><IcCard size={13} /> Kart</span></button>
                </div>
              )}
            </div>
            <div className="split-item-right">
              {p.paid ? (
                <span className="split-item-done" style={{display:"inline-flex",alignItems:"center",gap:4}}>✓ {p.method === "CASH" ? <IcCash size={13} /> : <IcCard size={13} />} Alındı</span>
              ) : i === currentIdx ? (
                <button className="split-item-btn" onClick={() => setConfirm(i)} disabled={paying}>Al</button>
              ) : (
                <span className="split-item-waiting">Bekliyor</span>
              )}
            </div>
          </div>
        ))}
      </div>
      {payMsg && <div className={`pay-msg${payMsg.err ? " err" : " ok"}`}>{payMsg.text}</div>}
    </div>
  );
}

export default function SessionView({ sessionId, onClose, onSessionClosed, onRequirePin }) {
  const [session, setSession]       = useState(null);
  const [summary, setSummary]       = useState(null);

  // Paket + ödenmemiş session'da kaza kapamayı önle → uyarı göster
  const guardedClose = () => {
    const isTakeaway = session?.service_type === "TAKEAWAY" || session?.table_code === "takeaway";
    const notFullyPaid = summary && summary.status !== "CLOSED" &&
                         (summary.paid_amount ?? 0) < (summary.total_amount ?? 0);
    if (isTakeaway && notFullyPaid && (summary?.total_amount ?? 0) > 0) {
      const ok = window.confirm(
        "Bu paket siparişte henüz tam ödeme alınmadı.\n" +
        "Şimdi çıkarsan sipariş açık kalır ve Sistem sekmesinde uyarı olarak listelenir.\n\n" +
        "Yine de çıkmak istiyor musun?"
      );
      if (!ok) return;
    }
    onClose();
  };
  const [loading, setLoading]       = useState(true);
  const [view, setView]             = useState("detail");
  const bodyRef                     = useRef(null);
  // Sekme değişince içeriği en üstten başlat (Detay'da aşağı inip Ödeme'ye
  // geçince scroll'un altta kalması hatasını önler)
  useEffect(() => { if (bodyRef.current) bodyRef.current.scrollTop = 0; }, [view]);
  const [payMethod, setPayMethod]   = useState("CASH");
  const [payAmount, setPayAmount]   = useState("");
  const [paying, setPaying]         = useState(false);
  const [payMsg, setPayMsg]         = useState(null);
  const [confirm, setConfirm]       = useState(null);
  const [splitPlan, setSplitPlan]   = useState(null);
  const [editOrder, setEditOrder]   = useState(null); // düzenlenen sipariş objesi
  const [showMove,  setShowMove]    = useState(false);

  // CLOSED session'ı pollamaktan kaçınmak için ref
  const summaryRef = useRef(null);

  useEffect(() => {
    loadSession();
    // 10 sn'de bir arka plan refresh — QR siparişleri barista görebilsin
    const interval = setInterval(() => {
      if (summaryRef.current?.status === "CLOSED") return; // kapandıysa dur
      loadSession({ silent: true });
    }, 10000);
    return () => clearInterval(interval);
  }, [sessionId]); // eslint-disable-line

  const loadSession = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const [detailRes, summaryRes] = await Promise.all([
        fetch(`${API}/api/sessions/${sessionId}`),
        fetch(`${API}/api/sessions/${sessionId}/payment-summary`),
      ]);
      const detail      = await detailRes.json();
      const summaryData = await summaryRes.json();
      setSession(detail);
      setSummary(summaryData);
      summaryRef.current = summaryData; // polling guard için güncelle
    } catch (e) { if (!silent) console.error("Session yüklenemedi:", e); }
    if (!silent) setLoading(false);
  };

  const startSplit = (count, amountEach) => {
    const ts = Date.now();
    setSplitPlan(Array.from({ length: count }, (_, i) => ({
      amount: amountEach, method: payMethod, paid: false,
      idempotencyKey: `split-${sessionId}-${i}-${ts}`,
    })));
    setPayMsg(null);
  };

  const executeConfirmedPay = async () => {
    const amount = confirm.amount;
    setPaying(true);
    setPayMsg(null);
    // Popup blocker'ı aşmak için pencereyi fetch'ten ÖNCE aç — SADECE son ödeme ise.
    // Kısmi ödeme (amount < remaining) için pencere açma; son ödemede (tüm kalan kapatılıyor) aç.
    const isFullPayment = amount >= remaining - 0.01;
    const receiptWin = isFullPayment ? window.open("about:blank", "_blank") : null;
    try {
      const res = await fetch(`${API}/api/sessions/${sessionId}/pay`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ amount, payment_method: payMethod, idempotency_key: confirm.idempotencyKey }),
      });
      const data = await res.json();
      if (!res.ok) {
        if (receiptWin) receiptWin.close();
        setPayMsg({ err: true, text: data.detail || "Ödeme başarısız" });
      } else if (data.is_fully_paid) {
        setConfirm(null);
        if (data.auto_print && receiptWin) {
          receiptWin.location.href = `${API}/api/sessions/${sessionId}/receipt?autoprint=true`;
        } else if (receiptWin) {
          receiptWin.close();
        }
        onSessionClosed(); onClose(); return;
      } else {
        if (receiptWin) receiptWin.close();
        setPayMsg({ err: false, text: `✓ ₺${fmtTL(amount)} alındı — Kalan: ₺${fmtTL(data.remaining_amount)}` });
        setPayAmount("");
        await loadSession();
      }
    } catch {
      if (receiptWin) receiptWin.close();
      setPayMsg({ err: true, text: "Bağlantı hatası" });
    }
    setConfirm(null);
    setPaying(false);
  };

  const handleCancelOrder = (orderId) => {
    if (!onRequirePin) return;
    onRequirePin(async () => {
      try {
        await fetch(`${API}/api/orders/${orderId}/status`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ status: "CANCELLED" }),
        });
        await loadSession();
      } catch {
        // sessizce geç — loadSession zaten hata durumunu handle eder
      }
    });
  };

  if (loading) return (
    <div className="session-overlay" onClick={onClose}>
      <div className="session-modal" onClick={e => e.stopPropagation()}>
        <div className="session-loading">Yükleniyor…</div>
      </div>
    </div>
  );

  // Session yüklendikten sonra guard aktif — üstteki `onClose`'ları guardedClose'a çeviriyoruz.

  if (!session || !summary) return null;

  const duration  = session.opened_at ? Math.round((Date.now() - new Date(session.opened_at).getTime()) / 60000) : 0;
  const remaining = summary.remaining_amount;
  const isPaid    = summary.status === "CLOSED";

  return (
    <div className="session-overlay" onClick={guardedClose}>
      <div className="session-modal" onClick={e => e.stopPropagation()}>

        {confirm && (
          <ConfirmPay amount={confirm.amount} method={payMethod} label={confirm.label}
            onConfirm={executeConfirmedPay} onCancel={() => setConfirm(null)} paying={paying} />
        )}

        {editOrder && (
          <EditOrderModal
            order={editOrder}
            onClose={() => setEditOrder(null)}
            onSaved={() => { setEditOrder(null); loadSession(); }}
          />
        )}

        {showMove && (
          <MoveTableModal
            sessionId={sessionId}
            currentTableId={session?.table_id}
            currentTableLabel={session?.table_label}
            onMoved={() => { setShowMove(false); loadSession(); }}
            onClose={() => setShowMove(false)}
          />
        )}

        <div className="session-header">
          <div className="session-table-info">
            <div className="session-table-label">{session.table_label}</div>
            <div className="session-meta">
              <span style={{display:"inline-flex",alignItems:"center",gap:5}}><IcClock size={13} /> {duration}dk</span>
              <span style={{display:"inline-flex",alignItems:"center",gap:5}}><IcBox size={13} /> {session.orders?.length || 0} sipariş</span>
              {summary.status === "PARTIALLY_PAID" && <span className="session-partial-badge">Kısmi Ödendi</span>}
            </div>
          </div>
          <div className="session-header-actions">
            <button className="session-close-btn" onClick={guardedClose}>✕</button>
          </div>
        </div>

        {summary.paid_amount > summary.total_amount + 0.01 && (
          <div className="session-overpayment-alert" style={{display:"inline-flex",alignItems:"center",gap:8}}>
            <IcWarning size={16} /> <span>Para üstü — <strong>₺{fmtTL(summary.paid_amount - summary.total_amount)}</strong> müşteriye iade edilmeli</span>
          </div>
        )}

        {!isPaid && (
          <div className="session-tabs">
            <button className={`session-tab${view === "detail" ? " active" : ""}`} onClick={() => { setView("detail"); setSplitPlan(null); }}><span style={{display:"inline-flex",alignItems:"center",gap:6}}><IcClipboard size={14} /> Detay</span></button>
            {summary.total_amount > 0 && (
              <button className={`session-tab${view === "pay" ? " active" : ""}`} onClick={() => setView("pay")}><span style={{display:"inline-flex",alignItems:"center",gap:6}}><IcCard size={14} /> Ödeme</span></button>
            )}
          </div>
        )}

        <div className="session-body" ref={bodyRef}>
          {view === "detail" && (
            <>
              <div className="session-section">
                <div className="session-section-title"><IcClipboard size={15} /> Sipariş Özeti</div>
                <div className="session-items">
                  {session.aggregated_items?.map((item, i) => (
                    <div key={i} className="session-item">
                      <span className="si-qty">
                        {item.total_weight_grams
                          ? `${item.total_weight_grams}g`
                          : `${item.total_quantity}×`}
                      </span>
                      <span className="si-name">{item.product_name}</span>
                      <span className="si-price">₺{fmtTL(item.total_spent)}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="session-section">
                <div className="session-section-title"><IcClock size={15} /> Sipariş Geçmişi</div>
                <div className="session-timeline">
                  {session.orders?.map((order, i) => (
                    <div key={order.id} className={`timeline-order${order.status === "CANCELLED" ? " cancelled" : ""}`}>
                      <div className="timeline-order-header">
                        <span className="timeline-order-num">#{i + 1}</span>
                        <span className="timeline-order-time">{new Date(order.created_at).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Istanbul" })}</span>
                        {order.status === "CANCELLED"
                          ? <span className="timeline-order-cancelled">İptal</span>
                          : <span className="timeline-order-total">₺{fmtTL(order.total_amount)}</span>
                        }
                        {/* Edit butonu: oturum açıksa ve iptal edilmemişse göster (DONE dahil — manuel giriş düzeltme) */}
                        {!isPaid && order.status !== "CANCELLED" && (
                          <>
                            <button
                              className="timeline-edit-btn"
                              onClick={() => setEditOrder(order)}
                              title="Siparişi Düzenle"
                            >✏️</button>
                            <button
                              className="timeline-cancel-btn"
                              onClick={() => handleCancelOrder(order.id)}
                              title="Siparişi İptal Et"
                            >✕</button>
                          </>
                        )}
                      </div>
                      <div className="timeline-order-items">
                        {order.items?.map(item => (
                          <span key={item.id} className="timeline-item">
                            {item.weight_grams ? `${item.weight_grams}g` : `${item.quantity}×`} {item.product_name}
                          </span>
                        ))}
                      </div>
                      {order.note && <div className="timeline-order-note" style={{display:"inline-flex",alignItems:"center",gap:6}}><IcMemo size={12} /> {order.note}</div>}
                    </div>
                  ))}
                </div>
              </div>

              {/* Ödeme özeti — ödeme yapıldıysa göster */}
              {summary.payments?.length > 0 && (() => {
                const cashTotal = summary.payments.filter(p => p.payment_method === "CASH").reduce((s, p) => s + p.amount, 0);
                const cardTotal = summary.payments.filter(p => p.payment_method === "CARD").reduce((s, p) => s + p.amount, 0);
                return (
                  <div className="session-section">
                    <div className="session-section-title"><IcMoney size={17} /> Ödeme</div>
                    <div className="session-items">
                      {cashTotal > 0 && (
                        <div className="session-item">
                          <span className="si-qty"><IcCash size={13} /></span>
                          <span className="si-name">Nakit</span>
                          <span className="si-price">₺{fmtTL(cashTotal)}</span>
                        </div>
                      )}
                      {cardTotal > 0 && (
                        <div className="session-item">
                          <span className="si-qty"><IcCard size={13} /></span>
                          <span className="si-name">Kredi Kartı</span>
                          <span className="si-price">₺{fmtTL(cardTotal)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}
            </>
          )}

          {view === "pay" && !splitPlan && (
            <div className="pay-view">
              <div className="pay-summary-row"><span>Toplam</span><span>₺{fmtTL(summary.total_amount)}</span></div>
              {summary.paid_amount > 0 && <div className="pay-summary-row paid"><span>Ödendi</span><span>₺{fmtTL(summary.paid_amount)}</span></div>}
              <div className="pay-summary-row remaining"><span>Kalan</span><span>₺{fmtTL(remaining)}</span></div>

              <div className="pay-method-label">Ödeme Yöntemi</div>
              <div className="pay-method-btns">
                <button className={`pay-method-btn${payMethod === "CASH" ? " active" : ""}`} onClick={() => setPayMethod("CASH")}><IcCash size={15} /> Nakit</button>
                <button className={`pay-method-btn${payMethod === "CARD" ? " active" : ""}`} onClick={() => setPayMethod("CARD")}><IcCard size={15} /> Kredi Kartı</button>
              </div>

              <div className="pay-split-label">Hesabı Böl</div>
              <div className="pay-split-btns">
                {Object.entries(summary.split_suggestions).map(([key, val]) => {
                  const count = parseInt(key);
                  return (
                    <button key={key} className="pay-split-btn" onClick={() => startSplit(count, val)}>
                      {count} kişi<br /><strong>₺{fmtTL(val)}</strong>
                    </button>
                  );
                })}
              </div>

              <div className="pay-method-label" style={{marginTop: 4}}>Manuel Tutar</div>
              <label className="pay-fullamount-check">
                <input
                  type="checkbox"
                  checked={payAmount !== "" && Math.abs(parseFloat(payAmount) - remaining) < 0.01}
                  onChange={e => setPayAmount(e.target.checked ? String(remaining) : "")}
                />
                <span>Tamamını al</span>
                <span className="pay-fullamount-hint">₺{fmtTL(remaining)}</span>
              </label>
              <div className="pay-amount-row">
                <input className="pay-amount-input" type="number" placeholder="Tutar girin..." value={payAmount} onChange={e => setPayAmount(e.target.value)} />
                <button className="pay-partial-btn" disabled={!payAmount}
                  onClick={() => { const amt = parseFloat(payAmount); if (amt > 0) setConfirm({ amount: amt, label: "Kısmi Ödeme", idempotencyKey: `pay-${sessionId}-${Date.now()}` }); }}>Öde</button>
              </div>

              {payMsg && <div className={`pay-msg${payMsg.err ? " err" : " ok"}`}>{payMsg.text}</div>}

              {summary.payments?.length > 0 && (
                <div className="pay-history">
                  <div className="pay-history-title">Geçmiş Ödemeler</div>
                  {summary.payments.map(p => (
                    <div key={p.id} className="pay-history-row">
                      <span>{p.payment_method === "CASH" ? <IcCash size={13} /> : <IcCard size={13} />} {p.payment_method}</span>
                      <span>₺{fmtTL(p.amount)}</span>
                      <span className="pay-history-time">{new Date(p.created_at).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Istanbul" })}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {view === "pay" && splitPlan && (
            <SplitFlow splitPlan={splitPlan} setSplitPlan={setSplitPlan}
              sessionId={sessionId} onAllPaid={() => { onSessionClosed(); onClose(); }} onCancel={() => setSplitPlan(null)} />
          )}
        </div>

        <div className="session-footer">
          <div className="session-total">
            <span className="session-total-label">{remaining < summary.total_amount ? "Kalan" : "Toplam"}</span>
            <span className="session-total-amount">₺{fmtTL(remaining)}</span>
          </div>
          <div className="session-footer-actions">
            {(summary.status === "OPEN" || summary.status === "PARTIALLY_PAID") &&
             (session?.service_type ?? "TABLE") === "TABLE" && (
              <button className="session-receipt-btn" onClick={() => setShowMove(true)} title="Masayı Taşı">
                ↔ Taşı
              </button>
            )}
            {/* Adisyon butonu her zaman görünür */}
            <button
              className="session-receipt-btn"
              onClick={() => window.open(`${API}/api/sessions/${sessionId}/receipt?source=manual`, "_blank")}
              title="Adisyon Önizle"
            >
              Adisyon
            </button>
            {!isPaid && view === "pay" && !splitPlan && (
              <button className="session-close-bill-btn" onClick={() => setConfirm({ amount: remaining, label: "Tüm Hesap", idempotencyKey: `pay-${sessionId}-${Date.now()}` })}>
                {payMethod === "CASH" ? <IcCash size={15} /> : <IcCard size={15} />} Tamamını Al
              </button>
            )}
            {!isPaid && view === "detail" && (
              <button className="session-close-bill-btn" onClick={() => setView("pay")}><IcCard size={15} /> Hesabı Al</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
